import { Controller } from '@hotwired/stimulus'
import { isAfter, isDate, isFuture, subYears } from 'date-fns'
import { isEqual } from 'date-fns/fp'

// Connects to data-controller="new-account-form"
export default class extends Controller {
  static targets = [
    'password',
    'passwordConfirmation',
    'plan',
    'planPrice',
    'couponCode',
    'couponStatus',
    'errors',
  ]
  static values = {
    locale: String,
    submitDisable: String,
    submitEnable: String,
    validations: Object,
  }

  connect() {
    this.form = this.element.querySelector('form')
    this.submittingModal = this.element.querySelector('#submitting-modal > div')
    this.submitButton = this.form.querySelector('button[type=submit]')

    this.form.dataset['action'] = 'turbo:submit-start->new-account-form#disableForm'
    this.form.addEventListener('turbo:submit-end', this.formSubmitted.bind(this))

    if (!this.hasSubmitDisableValue) {
      this.submitDisableValue = 'Processing...'
    }

    this.prefillCouponFromQueryString()
  }

  prefillCouponFromQueryString() {
    if (!this.hasCouponCodeTarget) return

    const params = new URLSearchParams(window.location.search)
    const code = params.get('c')

    if (code) {
      this.couponCodeTarget.value = code.toUpperCase()
      this.validateCoupon({ currentTarget: this.couponCodeTarget })
    }
  }

  selectPlan(event) {
    const input = event.currentTarget
    this.toggleCheckedPlan(input)
  }

  toggleCheckedPlan(input) {
    this.planTargets.forEach(el => {
      if (input.checked && el === input.parentElement.parentElement.parentElement) {
        el.setAttribute('aria-checked', true)
      } else {
        el.removeAttribute('aria-checked')
      }
    })
  }

  validatePassword() {
    const password = this.passwordTarget
    const confirmation = this.passwordConfirmationTarget

    if (password.value.length < 8) {
      password.setCustomValidity(this.validationsValue.password.length)
    } else {
      password.setCustomValidity('')
    }

    if (password.value === confirmation.value) {
      confirmation.setCustomValidity('')
    } else {
      confirmation.setCustomValidity(this.validationsValue.password.confirmation_mismatch)
    }
  }

  validateFullName(event) {
    const input = event.currentTarget
    const pairs = input.value
      .trim()
      .split(' ')
      .map(name => name.trim())
    const isValid = pairs.length > 1 && pairs.every(pair => pair.length > 0)

    if (isValid) {
      input.setCustomValidity('')
    } else {
      input.setCustomValidity(this.validationsValue.full_name.incomplete)
    }
  }

  validateBirthday(event) {
    const input = event.currentTarget
    const date = new Date(input.value)
    if (!isDate(date)) {
      input.setCustomValidity(this.validationsValue.birthday.invalid)
      return
    }

    if (isFuture(date)) {
      input.setCustomValidity(this.validationsValue.birthday.past_required)
      return
    }

    const today = new Date()
    const eighteenYearsAgo = subYears(today, 18)
    if (isEqual(date, eighteenYearsAgo) || isAfter(date, eighteenYearsAgo)) {
      input.setCustomValidity(this.validationsValue.birthday.adult)
      return
    }

    input.setCustomValidity('')
  }

  validateCPFNumber(cpf) {
    if (cpf.length !== 11) {
      return false
    }

    const blacklisted = [
      '00000000000',
      '11111111111',
      '22222222222',
      '33333333333',
      '44444444444',
      '55555555555',
      '66666666666',
      '77777777777',
      '88888888888',
      '99999999999',
    ]

    if (blacklisted.includes(cpf)) {
      return false
    }

    const calculateDigit = (cpf, factor) => {
      const sum = cpf
        .split('')
        .slice(0, factor - 1)
        .reduce((acc, digit, index) => acc + parseInt(digit) * (factor - index), 0)
      const remainder = (sum * 10) % 11
      return remainder === 10 || remainder === 11 ? 0 : remainder
    }

    const firstDigit = calculateDigit(cpf, 10)
    const secondDigit = calculateDigit(cpf, 11)

    return firstDigit === parseInt(cpf[9]) && secondDigit === parseInt(cpf[10])
  }

  validateCPF(event) {
    const input = event.currentTarget
    const cpf = input.value.replace(/\D/g, '')
    const isValid = this.validateCPFNumber(cpf)
    if (isValid) {
      input.setCustomValidity('')
    } else {
      input.setCustomValidity(this.validationsValue.cpf.invalid)
    }
  }

  uppercaseCoupon(event) {
    const input = event.currentTarget
    input.value = input.value.toUpperCase()
  }

  async validateCoupon(event) {
    const input = event.currentTarget
    const code = input.value.trim()

    if (code === '') {
      this.resetPlanPrices()
      this.hideCouponStatus()
      return
    }

    this.showCouponStatus('validating')

    try {
      const response = await fetch(`/${this.localeValue}/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
        body: JSON.stringify({ code }),
      })

      if (!response.ok) {
        this.resetPlanPrices()
        this.showCouponStatus('invalid')
        return
      }

      const data = await response.json()

      if (data.valid) {
        this.applyDiscount(data)
        this.showCouponStatus('applied')
      } else {
        this.resetPlanPrices()
        this.showCouponStatus(data.reason || 'invalid')
      }
    } catch (_error) {
      this.resetPlanPrices()
      this.showCouponStatus('invalid')
    }
  }

  applyDiscount(data) {
    this.planTargets.forEach(plan => {
      const amountInCents = parseInt(plan.dataset.amountInCents, 10)
      const discountedAmount = this.calculateDiscount(amountInCents, data)
      const priceElement = plan.querySelector('[data-new-account-form-target="planPrice"]')

      if (priceElement) {
        priceElement.textContent = this.formatMoney(discountedAmount)
      }
    })
  }

  resetPlanPrices() {
    this.planTargets.forEach(plan => {
      const amountInCents = parseInt(plan.dataset.amountInCents, 10)
      const priceElement = plan.querySelector('[data-new-account-form-target="planPrice"]')

      if (priceElement) {
        priceElement.textContent = this.formatMoney(amountInCents)
      }
    })
  }

  calculateDiscount(amountInCents, data) {
    if (data.discount_type === 'percentage') {
      return Math.round(amountInCents * (1 - data.discount_value / 100))
    }

    return data.discount_value
  }

  formatMoney(amountInCents) {
    const value = (amountInCents / 100).toFixed(2).replace('.', ',')
    return `R$ ${value.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  showCouponStatus(state) {
    const status = this.couponStatusTarget
    const messageKey = state === 'not_found' ? 'invalid' : state

    status.textContent = status.dataset[messageKey] || ''
    status.classList.remove('text-juniper-600', 'text-stiletto-600', 'text-amulet-600')

    if (state === 'validating') {
      status.classList.add('text-juniper-600')
    } else if (state === 'applied') {
      status.classList.add('text-amulet-600')
    } else {
      status.classList.add('text-stiletto-600')
    }

    status.classList.remove('hidden')
  }

  hideCouponStatus() {
    this.couponStatusTarget.classList.add('hidden')
  }

  disableForm() {
    this.submitButton.innerHTML = this.submitDisableValue
    this.submitButton.disabled = true
    this.submittingModal.classList.toggle('hidden')
  }

  async formSubmitted(event) {
    if (event.detail.success) {
      const json = await event.detail.fetchResponse.response.json()
      location.href = json.payment_url
    } else {
      this.errorsTarget.scrollIntoView({ behavior: 'smooth' })
      this.submitButton.innerHTML = this.submitEnableValue
      this.submitButton.disabled = false
      this.submittingModal.classList.toggle('hidden')
    }
  }
}
